import { useState, useEffect } from 'react';
import api from '../services/api';

interface TemplateItem {
  id: string;
  pullType: 'bin' | 'shelves';
  category: string;
  partNumber: string;
  description: string;
  inventoryItemId: string | null;
  quantityPerUnit: number;
  quantitySource: 'fixed' | 'from_bom' | 'per_order';
  conditionField: string | null;
  conditionValue: string | null;
  sortOrder: number;
  active: boolean;
  notes: string | null;
  binLocation: string | null;
  imageUrl: string | null;
}

const PULL_TYPES = ['bin', 'shelves'] as const;
const QUANTITY_SOURCES = [
  { value: 'fixed', label: 'Fixed (per unit)' },
  { value: 'from_bom', label: 'From BOM' },
  { value: 'per_order', label: 'Per Order' },
];

const CONDITION_FIELDS = [
  { value: '', label: 'None' },
  { value: 'motor_type', label: 'Motor Type' },
  { value: 'system_type', label: 'System Type' },
];

export default function PartsPullTemplatePage() {
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingItem, setEditingItem] = useState<TemplateItem | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activePullType, setActivePullType] = useState<'bin' | 'shelves'>('bin');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    pullType: 'bin' as string,
    category: '',
    partNumber: '',
    description: '',
    quantityPerUnit: 1,
    quantitySource: 'fixed' as string,
    conditionField: '',
    conditionValue: '',
    sortOrder: 0,
    notes: '',
  });

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await api.get('/parts-pull/template');
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setSaving(true);
      await api.post('/parts-pull/template', formData);
      await loadTemplate();
      setShowAddForm(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingItem) return;
    try {
      setSaving(true);
      await api.patch(`/parts-pull/template/${editingItem.id}`, formData);
      await loadTemplate();
      setEditingItem(null);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this template item?')) return;
    try {
      await api.del(`/parts-pull/template/${id}`);
      await loadTemplate();
    } catch (err: any) {
      setError(err.message || 'Failed to delete item');
    }
  };

  const startEdit = (item: TemplateItem) => {
    setEditingItem(item);
    setFormData({
      pullType: item.pullType,
      category: item.category,
      partNumber: item.partNumber,
      description: item.description,
      quantityPerUnit: item.quantityPerUnit,
      quantitySource: item.quantitySource,
      conditionField: item.conditionField || '',
      conditionValue: item.conditionValue || '',
      sortOrder: item.sortOrder,
      notes: item.notes || '',
    });
    setShowAddForm(false);
  };

  const startAdd = () => {
    setEditingItem(null);
    resetForm();
    setFormData(prev => ({ ...prev, pullType: activePullType }));
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      pullType: activePullType,
      category: '',
      partNumber: '',
      description: '',
      quantityPerUnit: 1,
      quantitySource: 'fixed',
      conditionField: '',
      conditionValue: '',
      sortOrder: 0,
      notes: '',
    });
  };

  const cancelForm = () => {
    setEditingItem(null);
    setShowAddForm(false);
    resetForm();
  };

  // Group items by pull type then category
  const filteredItems = items.filter(i => i.pullType === activePullType);
  const categories = [...new Set(filteredItems.map(i => i.category))];

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-400">Loading template...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Parts Pull Template</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configure which parts are included in the BIN and SHELVES pull checklists
          </p>
        </div>
        <button
          onClick={startAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          + Add Item
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 font-bold">x</button>
        </div>
      )}

      {/* Pull Type Tabs */}
      <div className="flex gap-2 mb-6">
        {PULL_TYPES.map(type => (
          <button
            key={type}
            onClick={() => { setActivePullType(type); cancelForm(); }}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activePullType === type
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {type === 'bin' ? 'BIN Parts' : 'SHELF Parts'}
          </button>
        ))}
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingItem) && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            {editingItem ? 'Edit Item' : 'Add New Item'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pull Type</label>
              <select
                value={formData.pullType}
                onChange={e => setFormData({ ...formData, pullType: e.target.value })}
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="bin">BIN</option>
                <option value="shelves">SHELVES</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value.toUpperCase() })}
                placeholder="e.g., TRACKS, WEIGHT BAR"
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Part Number</label>
              <input
                type="text"
                value={formData.partNumber}
                onChange={e => setFormData({ ...formData, partNumber: e.target.value })}
                placeholder="e.g., 15111"
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Part description"
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={e => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Qty Per Unit</label>
              <input
                type="number"
                step="0.01"
                value={formData.quantityPerUnit}
                onChange={e => setFormData({ ...formData, quantityPerUnit: parseFloat(e.target.value) || 0 })}
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantity Source</label>
              <select
                value={formData.quantitySource}
                onChange={e => setFormData({ ...formData, quantitySource: e.target.value })}
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                {QUANTITY_SOURCES.map(qs => (
                  <option key={qs.value} value={qs.value}>{qs.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Condition Field</label>
              <select
                value={formData.conditionField}
                onChange={e => setFormData({ ...formData, conditionField: e.target.value })}
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                {CONDITION_FIELDS.map(cf => (
                  <option key={cf.value} value={cf.value}>{cf.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Condition Value</label>
              <input
                type="text"
                value={formData.conditionValue}
                onChange={e => setFormData({ ...formData, conditionValue: e.target.value })}
                placeholder="e.g., keder,alpha"
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={!formData.conditionField}
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
              <input
                type="text"
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes"
                className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={editingItem ? handleUpdate : handleAdd}
              disabled={saving || !formData.partNumber || !formData.category || !formData.description}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving...' : editingItem ? 'Update' : 'Add Item'}
            </button>
            <button
              onClick={cancelForm}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Items by Category */}
      {categories.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No items for {activePullType === 'bin' ? 'BIN' : 'SHELVES'} pull. Click "Add Item" to create one.
        </div>
      ) : (
        <div className="space-y-6">
          {categories.map(cat => (
            <div key={cat} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <h3 className="font-bold text-gray-900 dark:text-white">{cat}</h3>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredItems.filter(i => i.category === cat).map(item => (
                  <div
                    key={item.id}
                    className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 ${
                      editingItem?.id === item.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-bold text-blue-600 dark:text-blue-400 w-16">
                          {item.partNumber}
                        </span>
                        <span className="text-gray-900 dark:text-white truncate">{item.description}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          Qty: {item.quantityPerUnit}
                          {item.quantitySource === 'fixed' ? '/unit' : item.quantitySource === 'from_bom' ? ' (from BOM)' : ' /order'}
                        </span>
                        {item.conditionField && (
                          <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                            {item.conditionField}: {item.conditionValue}
                          </span>
                        )}
                        {item.binLocation && (
                          <span>Bin: {item.binLocation}</span>
                        )}
                        <span className="text-gray-400">#{item.sortOrder}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => startEdit(item)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <h4 className="font-bold text-gray-900 dark:text-white mb-2">Quantity Source Legend</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-400">
          <div><strong>Fixed (per unit)</strong> — qty x number of units in order</div>
          <div><strong>From BOM</strong> — quantity calculated from BOM (skipped if not in BOM)</div>
          <div><strong>Per Order</strong> — flat quantity per order (not multiplied by units)</div>
        </div>
      </div>
    </div>
  );
}
